import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { DATE_SHORTCUTS, isoOffset, fmtDate } from "@/lib/helpers";
import { DateQuickPick } from "@/components/DateQuickPick";
import { FrDateInput } from "@/components/FrDateInput";
import { Loader2, PlusCircle } from "lucide-react";

export default function ReportMissing() {
  const nav = useNavigate();
  const [establishments, setEstablishments] = useState([]);
  const [f, setF] = useState({ last_name: "", first_name: "", birth_date: "", transport_date: isoOffset(0), establishment_id: "", establishment_name: "", pmt_requested: "", available_from: "", comment: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/establishments").then((r) => setEstablishments(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setSaving(true);
    try {
      await api.post("/requests/quick-report", { ...f, birth_date: f.birth_date || null });
      toast.success("PMT manquante signalée ✅");
      nav("/driver");
    } catch (err) { setError(apiError(err.response?.data?.detail) || err.message); setSaving(false); }
  };

  const pmtBtn = (val, label, color) => {
    const active = f.pmt_requested === val;
    const cls = active
      ? { oui: "bg-green-500 text-white border-green-500", non: "bg-red-500 text-white border-red-500", inconnu: "bg-slate-500 text-white border-slate-500" }[val]
      : { oui: "bg-green-50 text-green-700 border-green-200", non: "bg-red-50 text-red-700 border-red-200", inconnu: "bg-slate-100 text-slate-600 border-slate-200" }[val];
    return (
      <button type="button" data-testid={`pmt-requested-${val}`} onClick={() => setF({ ...f, pmt_requested: val })}
        className={`h-14 rounded-2xl border-2 font-bold active:scale-[0.98] transition-transform ${cls}`}>{label}</button>
    );
  };

  return (
    <div className="space-y-5 pb-6">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-orange-500 text-white flex items-center justify-center"><PlusCircle className="w-6 h-6" /></div>
        <h1 className="text-2xl font-heading font-extrabold text-brand-900">Signaler une PMT manquante</h1>
      </div>

      <form onSubmit={submit} className="space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm space-y-3">
          <p className="text-sm font-semibold uppercase tracking-wide text-slate-400">Patient</p>
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="report-lastname" placeholder="Nom" value={f.last_name} onChange={set("last_name")} className="h-12" required />
            <Input data-testid="report-firstname" placeholder="Prénom" value={f.first_name} onChange={set("first_name")} className="h-12" required />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-400">Date de naissance <span className="font-normal">(facultative)</span></label>
            <FrDateInput value={f.birth_date} onChange={(v) => setF({ ...f, birth_date: v })} mode="birth" testid="report-birthdate" className="mt-1" />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-400">Date du transport</label>
            <FrDateInput value={f.transport_date} onChange={(v) => setF({ ...f, transport_date: v })} testid="report-transport-date" className="mt-1" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-400">Lieu / établissement</label>
            <select data-testid="report-establishment" value={f.establishment_id} onChange={set("establishment_id")}
              className="mt-1 h-12 w-full rounded-lg border border-slate-200 bg-white px-3 text-slate-900">
              <option value="">— Sélectionner un établissement —</option>
              {establishments.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
            {!f.establishment_id && (
              <Input data-testid="report-establishment-name" placeholder="Ou saisir un nouvel établissement" value={f.establishment_name} onChange={set("establishment_name")} className="h-12 mt-2" />
            )}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200/70 p-5 shadow-sm space-y-3">
          <p className="text-sm font-semibold text-slate-600">La PMT a-t-elle été demandée ?</p>
          <div className="grid grid-cols-3 gap-2">
            {pmtBtn("oui", "Oui")}{pmtBtn("non", "Non")}{pmtBtn("inconnu", "Je ne sais pas")}
          </div>
          <p className="text-sm font-semibold text-slate-600 pt-1">Disponible à partir de quand ? <span className="text-slate-400 font-normal">(facultatif)</span></p>
          <DateQuickPick value={f.available_from} onChange={(v) => setF({ ...f, available_from: v })} testidPrefix="report-date" />
          <Textarea data-testid="report-comment" placeholder="Commentaire (facultatif)" value={f.comment} onChange={set("comment")} />
        </div>

        {error && <p data-testid="report-error" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>}
        <button type="submit" data-testid="report-submit" disabled={saving}
          className="w-full h-14 rounded-2xl bg-orange-500 text-white text-lg font-bold flex items-center justify-center active:scale-[0.98] transition-transform">
          {saving && <Loader2 className="w-5 h-5 mr-2 animate-spin" />}Envoyer le signalement
        </button>
      </form>
    </div>
  );
}
